// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';
import ApiService from '../../../../share/util/api/service';
import constants from '../../../../common/nodejs/constants';
import { computeObdOmtDTCTableItem } from '../../../../util/obd/diag';
import { OBD_ONBOARD_MONITOR_TEST_LIMIT } from "../../../../util/obd/functions";

export default {
  name: "obdOnboardMonitorTestPage",
  props: {
    // noop
  },
  data() {
    return {
      processing: false,
      recollectedData: [],
      measuredDataColumns: [],
      systemName: '',
      measuresId: '',
      obdDataId: '',
      selectedSystem: null,
      isObd: true,
      measuredDataItems: [],
      communicationStandards: constants.UPPER_LAYER_PROTOCOL_SAEJ1979
    };
  },

  watch: {
    language() {
      this.updateColumns(this.systemName);
    },
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
  },

  methods: {
    ...mapActions('auth', ['logout']),

    /**
     * DTC計測データ一覧取得
     * @param {String} obdDataId OBD Data ID
     * @param {String} measuresId 計測データID
     * @param {String} system ECU名
     * @param {String} systemNo ECU No
     */
    async createObdOnBoardMonitorTestMeasuresList(obdDataId, measuresId, system, systemNo) {
      systemNo;
      try {
        this.systemName = system;
        this.obdDataId = obdDataId;
        this.measuresId = measuresId;
        this.processing = true;

        let response = null;
        let results = null;
        const params = { last_evaluated_key: null, region_info: this.cachedProjectDataSource };
        do {
          response = await ApiService.getParamCall(
            ApiService.OBD,
            `/obd/${obdDataId}/measures/${measuresId}/onboardMonitorTest`,
            params,
            this.operationScreen
          );

          handler.validate(handler.validateTypes.all, response, this, null, () => {
            results = results ? results.concat(response.data.user_data.results) : response.data.user_data.results;
            params.last_evaluated_key = response.data.user_data.last_evaluated_key ? JSON.stringify(response.data.user_data.last_evaluated_key) : null;
          }, (result) => {
            if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
          });

          if (results?.length >= OBD_ONBOARD_MONITOR_TEST_LIMIT) break;
        } while (params.last_evaluated_key);

        if (results) {
          if (results.length >= OBD_ONBOARD_MONITOR_TEST_LIMIT) results = results.slice(0,OBD_ONBOARD_MONITOR_TEST_LIMIT);
          this.recollectedData = results;
          this.updateTable(response.data.user_data.communication_standards, results);
          this.updateColumns();
        }

      } finally {
        this.processing = false;
      }
    },

    /** 初期表示で必要な情報を設定
     */
    async updateColumns() {
      if (this.communicationStandards === constants.UPPER_LAYER_PROTOCOL_SAEJ1979) {
        this.measuredDataColumns = [
          { name: 'mid', label: 'fixed_resource.obd_item_mid' },
          { name: 'tid', label: 'fixed_resource.obd_item_tid' },
          { name: 'scaling_id', label: 'fixed_resource.obd_item_scalingid' },
          { name: 'test_value', label: 'fixed_resource.header_data_monitor_table_value', },
          { name: 'unit', label: 'fixed_resource.header_data_monitor_table_unit', },
          { name: 'minimum_test_limit', label: 'fixed_resource.header_data_monitor_table_min', },
          { name: 'maximum_test_limit', label: 'fixed_resource.header_data_monitor_table_max', },
          { name: 'result', label: 'fixed_resource.header_readiness_code_result', }
        ];
      } else {
        this.measuredDataColumns = [
          { name: 'status', label: 'message.header_vehicle_dtc_table_status' },
          { name: 'code', label: 'message.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
          { name: 'description', label: 'message.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
          { name: 'scaling_id', label: 'fixed_resource.obd_item_scalingid' },
          { name: 'test_value', label: 'fixed_resource.header_data_monitor_table_value', },
          { name: 'unit', label: 'fixed_resource.header_data_monitor_table_unit', },
          { name: 'minimum_test_limit', label: 'fixed_resource.header_data_monitor_table_min', },
          { name: 'maximum_test_limit', label: 'fixed_resource.header_data_monitor_table_max', },
          { name: 'result', label: 'fixed_resource.header_readiness_code_result', }
        ];
      }

    },

    /**
     * テーブル情報更新処理を行う
     * @param {object} communicationStandards レスポンスデータ
     * @param {object[]} results ECU No
     */
    updateTable(communicationStandards, results) {
      if (results) {
        this.communicationStandards = communicationStandards;
        this.measuredDataItems = computeObdOmtDTCTableItem(
          results,
          communicationStandards === constants.UPPER_LAYER_PROTOCOL_OBD_ON_UDS,
          this
        );
      }
    },

    /**
     * メモボタン押下.
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async parentOpenMemoModal() {
      this.$emit('openMemoModal');
    },

    /** DataRecollection.jsの関数を呼び出す */
    async debugExport() {
      await this.dtcTestDataExport();
    },

    /** DTCテストデータエクスポート  */
    dtcTestDataExport() {
      this.$emit('dtcTestDataExport', this.recollectedData);
    }
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },

    /**
     * 結果表示の表示フラグ
     */
    isShowTable() {
      return !this.processing;
    }
  },
};